import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Hook for managing anchor elements for MUI Menu/Popover components.
 *
 * Supports two patterns:
 * 1. Ref-based (recommended for react-aria): Use `ref` on button, `handleOpen` with onPress
 * 2. Event-based (legacy): Use `handleClick` with native onClick
 *
 * @example
 * // Ref-based pattern (for AgButton with onPress)
 * const { ref, anchorEl, open, handleOpen, handleClose } = useAnchorEl();
 * <AgButton ref={ref} onPress={handleOpen}>Open</AgButton>
 * <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>...</Menu>
 *
 * @example
 * // Event-based pattern (for native onClick)
 * const { anchorEl, open, handleClick, handleClose } = useAnchorEl();
 * <button onClick={handleClick}>Open</button>
 * <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>...</Menu>
 */
export default function useAnchorEl<T extends HTMLElement = HTMLButtonElement>() {
  const ref = useRef<T>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Lock to prevent immediate close after opening (fixes React Aria + MUI Menu race condition)
  const closeLockRef = useRef(false);

  // For ref-based pattern: open state is derived from whether we have a captured anchor
  const open = Boolean(anchorEl);

  // Ref-based: capture the ref's current element as anchor
  // Uses a lock to prevent MUI's ClickAwayListener from immediately closing the menu
  const handleOpen = useCallback(() => {
    if (ref.current) {
      closeLockRef.current = true;
      setAnchorEl(ref.current);
      // Release the lock after a short delay to allow the menu to fully render
      setTimeout(() => {
        closeLockRef.current = false;
      }, 100);
    }
  }, []);

  // Event-based (legacy): capture from event.currentTarget
  // Also uses the lock to prevent MUI's ClickAwayListener from immediately closing the menu
  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    closeLockRef.current = true;
    setAnchorEl(event.currentTarget);
    // Release the lock after a short delay to allow the menu to fully render
    setTimeout(() => {
      closeLockRef.current = false;
    }, 100);
  }, []);

  const handleClose = useCallback(() => {
    // Don't close if we're in the lock period after opening
    if (closeLockRef.current) {
      return;
    }
    setAnchorEl(null);
  }, []);

  // Force close that bypasses the lock - use for hover-based menus
  // where the close is an intentional user action, not a click-away
  const handleForceClose = useCallback(() => {
    closeLockRef.current = false;
    setAnchorEl(null);
  }, []);

  return useMemo(
    () => ({
      ref, // Attach to button for ref-based pattern
      anchorEl, // Use for Menu/Popover anchorEl prop
      open,
      handleOpen, // Use with onPress for ref-based pattern
      handleClick, // Use with onClick for event-based pattern
      handleClose,
      handleForceClose, // Use with onHoverEnd for hover-based menus
    }),
    [anchorEl, open, handleOpen, handleClick, handleClose, handleForceClose]
  );
}
